import { PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES } from "../data/product-feedback-attachment";

const DOM_SNAPSHOT_FAILURE =
  "We couldn't capture this screen. Turn it off or attach a PNG or JPEG instead.";

function canvasBlob(
  canvas: HTMLCanvasElement,
  type: "image/png" | "image/jpeg",
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error(DOM_SNAPSHOT_FAILURE));
        else resolve(blob);
      },
      type,
      quality,
    );
  });
}

async function boundedSnapshotBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const png = await canvasBlob(canvas, "image/png");
  if (png.size <= PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES) return png;

  for (const quality of [0.9, 0.82, 0.72, 0.62]) {
    const jpeg = await canvasBlob(canvas, "image/jpeg", quality);
    if (jpeg.size <= PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES) return jpeg;
  }

  let source = canvas;
  for (const ratio of [0.82, 0.68, 0.56]) {
    const scaled = document.createElement("canvas");
    scaled.width = Math.max(1, Math.round(canvas.width * ratio));
    scaled.height = Math.max(1, Math.round(canvas.height * ratio));
    const context = scaled.getContext("2d");
    if (context === null) break;
    context.drawImage(source, 0, 0, scaled.width, scaled.height);
    source = scaled;
    const jpeg = await canvasBlob(scaled, "image/jpeg", 0.72);
    if (jpeg.size <= PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES) return jpeg;
  }

  throw new Error(DOM_SNAPSHOT_FAILURE);
}

/** Renders only the current viewport; the feedback sheet and explicitly ignored nodes stay out. */
export async function captureCurrentConsoleScreen(): Promise<File> {
  try {
    const root = document.documentElement;
    // Keep the renderer out of the initial Console bundle; it is needed only after an explicit
    // feedback action and Vite emits it as a separate lazy chunk.
    // The Console design system resolves color-mix() tokens to modern color() values. The Pro
    // renderer supports those browser-computed colors while retaining the html2canvas API.
    const { default: html2canvas } = await import("html2canvas-pro");
    const canvas = await html2canvas(root, {
      allowTaint: false,
      backgroundColor: getComputedStyle(document.body).backgroundColor || "#0b1018",
      height: window.innerHeight,
      ignoreElements: (element) =>
        element.classList.contains("pfs-sheet") ||
        element.hasAttribute("data-feedback-screenshot-ignore"),
      imageTimeout: 3_000,
      logging: false,
      scale: Math.min(Math.max(window.devicePixelRatio || 1, 1), 2),
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      useCORS: true,
      width: window.innerWidth,
      windowHeight: document.documentElement.scrollHeight,
      windowWidth: document.documentElement.scrollWidth,
      x: window.scrollX,
      y: window.scrollY,
    });
    const blob = await boundedSnapshotBlob(canvas);
    const isPng = blob.type === "image/png";
    return new File([blob], isPng ? "current-screen.png" : "current-screen.jpg", {
      type: isPng ? "image/png" : "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    throw new Error(DOM_SNAPSHOT_FAILURE);
  }
}
